import React, { useContext, useEffect, useState } from 'react';
import {
    Row,
    Col,
    Form,
    Button,
    Grid,
    Input,
    Select,
    Space,
    Pagination,
    message,
} from 'antd';

import { ExportOutlined } from '@ant-design/icons';
import { Pencil, Plus } from 'lucide-react';
import { ListActionsBar } from '@/components/_components/ListActionsBar';
import { FilterBlock } from '@/components/FilterBlock';
import { PropsExcel } from '@/store/salles/CreateExcel/useCreateExcel';
import { ModalAddPlanogramFromProduct } from '@/components/ModalAddPlanogramFromProduct';
import {
    OptionalProductDataFilter,
    Planogram,
} from '../../store/Planogram/findAll';
import * as S from './style';
import { topTitleContext } from '../../contexts/TopTitle';
import { useGetAllStore } from '../../store/Store';

import { print } from './TemplateHtmlPlanogram';

import {
    planogramColumn,
    systemColumn,
    expectedMetricsColumn,
    currentMetricsColumns,
} from './columns';
import { PlanogramConfirm } from '../../components/Modals/PlanogramConfirm';
import { useUpdatePlanogramMutation } from '../../store/Planogram/update';
import { useExportPlanograms } from '../../store/Planogram/export';
import { useGetCategory } from '../../store/category';
import {
    PlanogramAutomationSteps,
    PlanogramUI,
    usePlanogramAutomationContext,
} from './context/PlanogramAutomation';
import { ModalPlanogram } from './_components/Modal';
import {
    PlanogramTableBlock,
    PlanogramTableType,
} from './_components/Tables/PlanogramData';
import { useConfirmExit } from './hooks/useConfirmExit';
import {
    OPTIONAL_PRODUCT_DATA_OPTIONS,
    PLANOGRAM_MONITORED_FIELDS,
} from './const';

interface PlanogramListExport {
    product_name: string;
    standard_amount: number;
    stock_amount: number;
    bar_code: string;
    amount_to_transfer: number;
}

const PlanogramAutomationScreen = () => {
    const { titleValue } = useContext(topTitleContext);
    const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);

    const [openModal, setOpenModal] = useState(false);
    const [openModalSavePlanogram, setOpenModalSavePlanogram] = useState(false);

    const [
        openModalAddPlanogramFromProduct,
        setOpenModalAddPlanogramFromProduct,
    ] = useState(false);

    const {
        planogramList,
        isLoadingPlanogram,
        params,
        setParams,
        refetch,
        isFetching,
        planograms,
        planogramsCopy,
        planogramChecked,
        setPlanogramsChecked,
        planogramsUpdated,
        setPlanogramsUpdated,
        setCurrentStep,
    } = usePlanogramAutomationContext();

    const [meta, setMeta] = useState({
        page: 1,
        last_page: 1,
        total: 0,
        perPage: 10,
    });
    const [editTable, setEditTable] = useState(false);

    const { data: categoryList } = useGetCategory();

    const categoryOptions = categoryList?.map((category) => ({
        label: category.name,
        value: `${category.id}`,
    }));

    useConfirmExit(planogramsUpdated.length > 0);

    const {
        isLoading: isLoadingExportPlanogram,
        mutateAsync: mutateAsyncExportPlanogram,
    } = useExportPlanograms(params);

    const exportParams: PropsExcel = {
        reportType: 'planogram-automation',
        optionalDataFilter: params.optionalDataFilter,
        filters: {
            stores: params['filters[stores][]'].map(Number),
            categories: params['filters[categories][]'].map(Number),
            product: params['filters[product]'] || undefined,
            barCode: params['filters[barCode]'] || undefined,
        },
    };

    const { mutateAsync, isLoading: isLoadingUpdatedPlanogram } =
        useUpdatePlanogramMutation();

    const [form] = Form.useForm();
    const [formplanogram] = Form.useForm();
    const [formPlanogramCreate] = Form.useForm();
    const breakpoints = Grid.useBreakpoint();

    const confirmPlanogramChange = Form.useWatch('confirm', formplanogram);

    const { data: stores, isLoading } = useGetAllStore();

    const storesOptions = stores?.map((store) => ({
        label: store.name,
        value: `${store.id}`,
    }));

    const onSelectChange = (newSelectedRowKeys: any) => {
        setSelectedRowKeys(newSelectedRowKeys);
        let planogramsSavedAsMarked: PlanogramUI[] = [...planogramChecked];

        for (let i = 0; i < newSelectedRowKeys.length; i += 1) {
            const findPlanogram = planograms.find(
                (item) => item.id === newSelectedRowKeys[i],
            );
            const planogramExistInArray = planogramsSavedAsMarked.find(
                (item) => item.id === newSelectedRowKeys[i],
            );

            if (!findPlanogram) {
                // eslint-disable-next-line no-continue
                continue;
            }

            if (findPlanogram && !planogramExistInArray) {
                planogramsSavedAsMarked.push({
                    ...findPlanogram,
                    deleted: false,
                    edited: false,
                });
            }
        }

        if (newSelectedRowKeys.length !== planogramsSavedAsMarked.length) {
            const removedLines = planogramsSavedAsMarked.filter((item) => {
                if (newSelectedRowKeys.includes(item.id as never)) {
                    return item;
                }
            });
            planogramsSavedAsMarked = removedLines;
        }
        setPlanogramsChecked(planogramsSavedAsMarked);
    };

    const selectRow = (record: Planogram) => {
        const selectedRowKeysCopy = [...selectedRowKeys];
        if (selectedRowKeysCopy.indexOf(record.id as never) >= 0) {
            selectedRowKeysCopy.splice(
                selectedRowKeysCopy.indexOf(record.id as never),
                1,
            );
        } else {
            selectedRowKeysCopy.push(record.id as never);
        }
        onSelectChange(selectedRowKeysCopy);
        setSelectedRowKeys(selectedRowKeysCopy);
    };

    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
        columnWidth: 0,
        preserveSelectedRowKeys: true,
        selectionRenderer: () => null,
    };

    const rowSelectionFirstTable = {
        selectedRowKeys,
        onChange: onSelectChange,
        preserveSelectedRowKeys: true,
    };

    const filterOption = (
        input: string,
        option?: { label: string; value: string },
    ) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase());

    const setCurrentPage = (value: number, pzSize: number) => {
        setMeta((prev) => {
            return { ...prev, page: value, perPage: pzSize };
        });
        setParams({
            ...params,
            page: value,
            perPage: pzSize,
        });
    };

    const onSubmit = (data: any) => {
        setParams({
            ...params,
            'filters[stores][]': data?.store ?? [],
            'filters[categories][]': data?.category ?? [],
            'filters[barCode]': data?.barcode,
            'filters[product]': data?.product,
            optionalDataFilter:
                data?.optional_product_data ?? OptionalProductDataFilter.ALL,
            page: 1,
        });
        setSelectedRowKeys([]);
    };

    const onReset = () => {
        form.resetFields();
        setParams({
            ...params,
            'filters[barCode]': '',
            'filters[product]': '',
            'filters[stores][]': [],
            'filters[categories][]': [],
            optionalDataFilter: OptionalProductDataFilter.ALL,
        });
    };

    const onCancel = () => {
        setEditTable(false);
        setSelectedRowKeys([]);
        setPlanogramsUpdated([]);
        setPlanogramsChecked([]);
        refetch();
    };

    const onSavePlanogram = async () => {
        try {
            let checkPlanogramUpdated = planogramsUpdated;
            if (!planogramsUpdated.length) {
                setOpenModalSavePlanogram(false);
                message.warning(
                    'Nenhum planograma foi modificado, tente novamente.',
                );
                return;
            }
            if (planogramsUpdated.length !== selectedRowKeys.length) {
                const planogramsToBeSaved = checkPlanogramUpdated.filter(
                    (item) => selectedRowKeys.includes(item.id as never),
                );
                checkPlanogramUpdated = planogramsToBeSaved;
            }
            /**
             * transform lines
             */
            const updateLinesWithoutAfter = checkPlanogramUpdated.map(
                (item) => ({
                    ...item,
                    price_final_in_cents:
                        item.price_final_in_cents_after ||
                        item.price_final_in_cents,
                    price_list_in_cents:
                        item.price_list_in_cents_after ||
                        item.price_list_in_cents,
                }),
            );

            /**
             * Remove line that should not be sent to update
             */
            const updateLinesWithoutAfterAndRemovedLines =
                updateLinesWithoutAfter.filter((item) => {
                    if (item.should_remove_line) {
                        return false;
                    }

                    return true;
                });
            setOpenModalSavePlanogram(false);
            await mutateAsync(updateLinesWithoutAfterAndRemovedLines);
            setSelectedRowKeys([]);
            setPlanogramsUpdated([]);
            setPlanogramsChecked([]);
            setCurrentStep(PlanogramAutomationSteps.products);
            message.success('Planograma Atualizado');
            formplanogram.resetFields();
            setEditTable(false);
            refetch();
        } catch (error) {
            setEditTable(false);
            message.error(
                'Aconteceu algum problema. Tente novamente mais tarde',
            );
        }
    };

    useEffect(() => {
        titleValue('Automação de Planograma');
    }, []);

    useEffect(() => {
        if (planogramList) {
            setMeta({
                ...meta,
                page: planogramList.meta.current_page,
                total: planogramList.meta.total,
                last_page: planogramList.meta.last_page,
            });
        }
    }, [planogramList?.data, isFetching]);

    useEffect(() => {
        const keys = planogramChecked.map((item) => item.id);
        setSelectedRowKeys(keys);
    }, [planogramChecked]);

    const rowClassName = (record: any) => {
        const stylesRow =
            record.deleted && record.edited
                ? `pointed deleted edited`
                : record.edited
                ? 'pointed edited'
                : 'pointed';
        return stylesRow;
    };

    const calculateItemsForExport = (
        response: Planogram[],
    ): PlanogramListExport[] => {
        const sumItems: { [productName: string]: PlanogramListExport } = {};

        response.forEach((item) => {
            const productName = item.product.name;
            if (!sumItems[productName]) {
                sumItems[productName] = {
                    product_name: productName,
                    standard_amount: 0,
                    stock_amount: 0,
                    bar_code: item.product.BarCodes?.[0]?.code || '',
                    amount_to_transfer: 0,
                };
            }
            sumItems[productName].standard_amount += item.standard_amount;
            sumItems[productName].stock_amount += item.stock_amount;
        });

        const formatToExport = Object.values(sumItems).map((item) => {
            const amount_to_transfer =
                item.stock_amount > item.standard_amount
                    ? 0
                    : item.standard_amount - item.stock_amount;

            return {
                product_name: item.product_name,
                bar_code: item.bar_code,
                standard_amount: item.standard_amount,
                stock_amount: item.stock_amount,
                amount_to_transfer,
            };
        });

        return formatToExport;
    };

    const exportPlanogramToPdf = async () => {
        try {
            const response = await mutateAsyncExportPlanogram();

            if (response.length <= 0) {
                message.warning('Nenhum planograma encontrado.');
                return;
            }

            const uniqueStoreNames = [
                ...new Set(
                    response.map((item: Planogram) => item?.store?.name),
                ),
            ];

            const storesName = uniqueStoreNames.join(', ');
            const formatToExport = calculateItemsForExport(response);
            print(formatToExport, {
                name: storesName,
            });
            message.success('Planogramas exportado com sucesso.');
        } catch (e) {
            message.error('Falha ao exportar a lista de planogramas.');
        }
    };

    const enableButton =
        planogramsUpdated.length > 0 || selectedRowKeys.length > 0 || editTable;

    const disableButton =
        !confirmPlanogramChange || planogramsUpdated.length === 0;

    return (
        <Row style={{ padding: '15px 20px' }}>
            <Col span={24}>
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={onSubmit}
                    initialValues={{ showFilter: 'filter' }}
                >
                    <ListActionsBar
                        type="planogram-automation"
                        exportLabel="planograma_automacao"
                        filters={{
                            optionalDataFilter: exportParams.optionalDataFilter,
                            filters: exportParams.filters,
                        }}
                        monitoredFields={PLANOGRAM_MONITORED_FIELDS}
                        exportProps={{
                            subtitle:
                                'Exportando planogramas com os filtros ativos',
                            generateLabel: 'Exportar em Excel',
                            extraActions: (
                                <Button
                                    icon={<ExportOutlined />}
                                    onClick={exportPlanogramToPdf}
                                    loading={isLoadingExportPlanogram}
                                >
                                    Exportar em PDF
                                </Button>
                            ),
                        }}
                        rightExtra={
                            !editTable && (
                                <S.AddProductButton
                                    icon={<Plus size={17} />}
                                    onClick={() =>
                                        setOpenModalAddPlanogramFromProduct(
                                            true,
                                        )
                                    }
                                >
                                    Adicionar produto(s) a planograma(s)
                                </S.AddProductButton>
                            )
                        }
                    >
                        <FilterBlock
                            onReset={onReset}
                            loading={isLoadingPlanogram}
                        >
                            <Col xs={24} md={6}>
                                <Form.Item label="Loja" name="store">
                                    <Select
                                        options={storesOptions}
                                        mode="multiple"
                                        maxTagCount="responsive"
                                        style={{ width: '100%' }}
                                        placeholder="Selecionar Loja..."
                                        showSearch
                                        filterOption={filterOption}
                                        disabled={isLoading}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={6}>
                                <Form.Item label="Produto" name="product">
                                    <Input placeholder="Nome do produto" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={6}>
                                <Form.Item
                                    label="Código de Barras"
                                    name="barcode"
                                >
                                    <Input allowClear maxLength={20} />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={6}>
                                <Form.Item label="Categoria" name="category">
                                    <Select
                                        options={categoryOptions}
                                        mode="multiple"
                                        maxTagCount="responsive"
                                        style={{ width: '100%' }}
                                        placeholder="Selecionar uma categoria..."
                                        showSearch
                                        filterOption={filterOption}
                                        disabled={isLoading}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={6}>
                                <Form.Item
                                    label="Dados opcionais de produto"
                                    name="optional_product_data"
                                >
                                    <Select
                                        options={OPTIONAL_PRODUCT_DATA_OPTIONS}
                                        style={{ width: '100%' }}
                                        placeholder="Exibir todos os produtos"
                                        allowClear
                                        showSearch
                                        disabled={isLoading}
                                    />
                                </Form.Item>
                            </Col>
                        </FilterBlock>
                    </ListActionsBar>
                </Form>
            </Col>

            <S.ResultRow>
                <S.TitleResult>
                    A pesquisa retornou {meta.total} resultado(s)
                </S.TitleResult>
                <Space size={12}>
                    {selectedRowKeys.length > 0 && (
                        <>
                            <S.SelectedBadge>
                                {selectedRowKeys.length} selecionado(s)
                            </S.SelectedBadge>
                            <Button
                                type="link"
                                size="small"
                                danger
                                onClick={onCancel}
                            >
                                Cancelar edição
                            </Button>
                        </>
                    )}
                    {(editTable || selectedRowKeys.length > 0) && (
                        <Button
                            type="primary"
                            icon={<Pencil size={15} />}
                            disabled={selectedRowKeys.length === 0}
                            onClick={() => setOpenModal(true)}
                        >
                            Alterar produtos selecionados
                        </Button>
                    )}
                    {selectedRowKeys.length <= 0 && !editTable && (
                        <Button
                            type="primary"
                            icon={<Pencil size={15} />}
                            onClick={() => setEditTable(true)}
                        >
                            Editar
                        </Button>
                    )}
                </Space>
            </S.ResultRow>
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'auto',
                }}
            >
                <S.ContainerTables>
                    <PlanogramTableBlock
                        title="Dados de Planograma"
                        columns={planogramColumn}
                        rowSelection={
                            editTable ? rowSelectionFirstTable : undefined
                        }
                        typeTable={PlanogramTableType.PLANOGRAM}
                        dataSource={planogramsCopy}
                        editTable={editTable}
                        rowClassName={rowClassName}
                        selectRow={selectRow}
                        loading={isLoadingPlanogram || isFetching}
                    />

                    <PlanogramTableBlock
                        title="Dados de Sistema"
                        columns={systemColumn}
                        rowSelection={rowSelection}
                        typeTable={PlanogramTableType.SYSTEM}
                        dataSource={planogramsCopy}
                        editTable={editTable}
                        rowClassName={rowClassName}
                        selectRow={selectRow}
                        loading={isLoadingPlanogram || isFetching}
                        className="table-to-hide"
                    />

                    <PlanogramTableBlock
                        title="Métricas Esperadas"
                        columns={expectedMetricsColumn}
                        rowSelection={rowSelection}
                        typeTable={PlanogramTableType.EXPECTED_METRICS}
                        dataSource={planogramsCopy}
                        editTable={editTable}
                        rowClassName={rowClassName}
                        selectRow={selectRow}
                        loading={isLoadingPlanogram || isFetching}
                        className="table-to-hide"
                    />

                    <PlanogramTableBlock
                        title="Métricas Atuais"
                        columns={currentMetricsColumns}
                        rowSelection={rowSelection}
                        typeTable={PlanogramTableType.CURRENT_METRICS}
                        dataSource={planogramsCopy}
                        editTable={editTable}
                        rowClassName={rowClassName}
                        selectRow={selectRow}
                        loading={isLoadingPlanogram || isFetching}
                        className="table-to-hide"
                    />
                </S.ContainerTables>
                <div
                    style={{
                        width: '100%',
                        height: 40,
                        display: 'flex',
                        justifyContent: 'flex-end',
                        marginTop: 20,
                    }}
                >
                    <Pagination
                        total={meta.total}
                        current={meta.page}
                        onChange={(page, pzSize) => {
                            setCurrentPage(page, pzSize);
                        }}
                    />
                </div>
            </div>

            {enableButton && (
                <div style={{ width: '100%', marginTop: 20 }}>
                    <Row
                        align="middle"
                        gutter={[16, 10]}
                        style={{ display: 'flex', justifyContent: 'center' }}
                    >
                        <Col
                            xs={24}
                            sm={24}
                            md={8}
                            lg={6}
                            xl={6}
                            xxl={4}
                            style={{ height: 32 }}
                        >
                            {enableButton && (
                                <Button
                                    htmlType="reset"
                                    danger
                                    style={{
                                        width: '100%',
                                        maxWidth: breakpoints.md ? 227 : 'none',
                                    }}
                                    onClick={onCancel}
                                    disabled={!enableButton}
                                >
                                    Cancelar
                                </Button>
                            )}
                        </Col>
                        <Col
                            xs={24}
                            sm={24}
                            md={8}
                            lg={6}
                            xl={6}
                            xxl={4}
                            style={{ height: 32 }}
                        >
                            {!!planogramsUpdated.length && (
                                <Button
                                    htmlType="submit"
                                    className="default-btn"
                                    style={{
                                        width: '100%',
                                        maxWidth: breakpoints.md ? 227 : 'none',
                                    }}
                                    disabled={disableButton}
                                    onClick={() =>
                                        setOpenModalSavePlanogram(true)
                                    }
                                    loading={isLoadingUpdatedPlanogram}
                                >
                                    {isLoadingUpdatedPlanogram
                                        ? 'Salvando...'
                                        : 'Salvar'}
                                </Button>
                            )}
                        </Col>
                    </Row>
                    <Form
                        form={formplanogram}
                        layout="vertical"
                        initialValues={{
                            confirm: false,
                            standard_amount_enabled: false,
                        }}
                        style={{ height: '100%' }}
                    >
                        <ModalPlanogram
                            open={openModal}
                            setOpenModal={setOpenModal}
                        />
                    </Form>
                </div>
            )}

            <PlanogramConfirm
                type="save"
                open={openModalSavePlanogram}
                onCancel={() => {
                    setOpenModalSavePlanogram(false);
                    setSelectedRowKeys([]);
                    setPlanogramsUpdated([]);
                    setEditTable(false);
                    refetch();
                }}
                onConfirm={() => onSavePlanogram()}
            />

            <Form
                layout="vertical"
                form={formPlanogramCreate}
                initialValues={{ step: 1 }}
            >
                <ModalAddPlanogramFromProduct
                    open={openModalAddPlanogramFromProduct}
                    onOk={() => setOpenModalAddPlanogramFromProduct(false)}
                    onCancel={() => setOpenModalAddPlanogramFromProduct(false)}
                />
            </Form>
        </Row>
    );
};

export default PlanogramAutomationScreen;
